<script setup lang="ts">
import debounce from "lodash.debounce";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker";
import { onBeforeMount } from "vue";
import { useDtfStore } from "../store";
import type { Standard, EnhancementTier } from "../types";
import { Tabs, TabButton } from "./components/Tabs";
import PrintAsIs from "./PrintAsIs/PrintAsIs.vue";
import PrintPerfect from "./PrintPerfect/PrintPerfect.vue";
import Custom from "./Custom/Custom.vue";
import DesignServices from "./DesignServices/DesignServices.vue";

const props = defineProps<{
  index: number;
  artwork: Standard.Artwork;
  reorderEnabled: boolean;
  paidExpertEditAddonEnabled?: boolean;
  activeConfigSection?: string;
  isMultiUpload?: boolean;
}>();

const emit = defineEmits(["set-active-config-section"]);

const store = useDtfStore();

const debouncedToggleEnhancement = debounce(async (section: EnhancementTier) => {
  if (!props.artwork?.id) {
    return;
  }

  const enableEnhancement = section !== "print_original";

  await store.updateEnableEnhancement({
    artworkId: props.artwork.id,
    enableEnhancement,
    enhancementTier: section
  });

  store.updateArtworkValuesById({
    id: props.artwork.id,
    hasExpertEditAddon: props.artwork.hasExpertEditAddon
  });
}, 100);

function setActiveConfigSection(section: EnhancementTier) {
  if (props.artwork.enhancementCompleted) {
    debouncedToggleEnhancement(section);
  } else {
    store.updateArtworkValuesById({
      id: props.artwork.id,
      enhancementTier: section,
      pricing: (store.cachedArtworkPrices as any)[section] || {},
      hasExpertEditAddon: props.artwork.hasExpertEditAddon
    });
  }

  trackRudderstackImageEdited({
    // eslint-disable-next-line no-nested-ternary
    product_line: store.sticker ? "sticker" : store.isProColor ? "pro color" : "dtf",
    artwork: {
      ...props.artwork,
      enhancementTier: section
    },
    edit_type: section === "print_original" ? "Print as uploaded" : "Jiffy Print Perfect",
    artworks_count: store.artworks.length,
    location: "pdp",
    pre_cut_transfers: store.preCut
  });
}

function setPrintPerfectSection() {
  setActiveConfigSection("full_enhancements");
}

// Trigger before component mounts (renders)
onBeforeMount(() => {
  setPrintPerfectSection();
});
</script>

<template>
  <Tabs class="artwork-quality-config-panel">
    <template #buttons>
      <TabButton
        :active="artwork.enhancementTier === 'print_original'"
        :icon="undefined"
        @click="setActiveConfigSection('print_original')"
      >
        Print As Is
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'full_enhancements'"
        :icon="'stars-corners'"
        @click="setActiveConfigSection('full_enhancements')"
      >
        PrintPerfect
      </TabButton>

      <TabButton
        v-if="false"
        :active="artwork.enhancementTier === 'custom'"
        :icon="undefined"
        @click="setActiveConfigSection('custom')"
      >
        Expert Edit
      </TabButton>
    </template>

    <PrintAsIs
      v-if="artwork.enhancementTier === 'print_original'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :index="index"
      @print-perfect-click="setPrintPerfectSection"
    />
    <PrintPerfect
      v-if="artwork.enhancementTier === 'full_enhancements'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :reorder-enabled="reorderEnabled"
      :paid-expert-edit-addon-enabled="paidExpertEditAddonEnabled"
      :index="index"
    />
    <Custom
      v-if="!paidExpertEditAddonEnabled && artwork.enhancementTier === 'custom'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :index="index"
      :reorder-enabled="reorderEnabled"
    />
  </Tabs>

  <DesignServices
    v-if="artwork.enhancementTier === 'full_enhancements'"
    :artwork="artwork"
    :paid-expert-edit-addon-enabled="Boolean(paidExpertEditAddonEnabled)"
    :index="index"
    :is-multi-upload="Boolean(isMultiUpload)"
  />
</template>

<style scoped lang="scss">
@import "./ArtworkQualityConfigPanel.scss";

.artwork-quality-config-panel {
  padding: 0;
  container: #{$container-name} / inline-size;
}
</style>
